import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import RevenueChart from '../../components/Charts/RevenueChart';
import { fetchAllUsers, fetchAllEmployees, fetchAllParkingLot } from '../../services/adminService';
import { Container, Row, Col, Card, Badge, ProgressBar, Button } from 'react-bootstrap';
import './AdminHomepage.scss';

const AdminHomepage = () => {
    const admin = useSelector(state => state.admin || state.user);
    const navigate = useNavigate();
    
    const [realStats, setRealStats] = useState({
        totalUsers: 0,
        totalEmployees: 0,
        totalCapacity: 0,
        totalOccupied: 0,
        parkingLots: [],
        lastUpdated: new Date()
    });
    const [loading, setLoading] = useState(true);

    const loadRealtimeData = useCallback(async () => {
        try {
            const [usersRes, employeesRes, parkingRes] = await Promise.all([
                fetchAllUsers(1, 1000),
                fetchAllEmployees(1, 1000),
                fetchAllParkingLot()
            ]);

            const usersCount = usersRes?.data?.users?.length || usersRes?.data?.totalUsers || 0;
            const employeesCount = employeesRes?.data?.users?.length || employeesRes?.data?.totalEmployees || 0;
            const lots = parkingRes?.data || parkingRes || [];

            let capacity = 0;
            let occupied = 0;

            if (Array.isArray(lots)) {
                lots.forEach(lot => {
                    capacity += parseInt(lot.capacity || lot.totalSpot || 100, 10);
                    occupied += parseInt(lot.currentVehicles || lot.occupiedSpot || 0, 10);
                });
            }

            setRealStats({
                totalUsers: usersCount,
                totalEmployees: employeesCount,
                totalCapacity: capacity || 500,
                totalOccupied: occupied,
                parkingLots: Array.isArray(lots) ? lots : [],
                lastUpdated: new Date()
            });
        } catch (error) {
            console.error('Error fetching realtime admin stats:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRealtimeData();
        const interval = setInterval(() => {
            loadRealtimeData();
        }, 5000); // Realtime poll every 5s

        return () => clearInterval(interval);
    }, [loadRealtimeData]);

    const isLoggedIn = admin?.isAuthenticated || admin?.adminData?.adminCode || admin?.userData?.adminCode;

    const occupancyRate = realStats.totalCapacity > 0 
        ? Math.round((realStats.totalOccupied / realStats.totalCapacity) * 100) 
        : 0;

    // Login Prompt for unauthenticated admins
    if (!isLoggedIn) {
        return (
            <div className="admin-login-prompt py-5">
                <Container>
                    <Row className="justify-content-center text-center">
                        <Col lg={8} md={10}>
                            <Card className="border-0 shadow-lg p-5 rounded-4 bg-white">
                                <div className="admin-hero-icon mb-3">
                                    <i className="bi bi-shield-lock-fill text-primary display-3"></i>
                                </div>
                                <h2 className="fw-extrabold text-dark mb-2">Hệ Thống Quản Lý Bãi Đỗ Xe IUH Parking</h2>
                                <p className="text-secondary mb-4">
                                    Cổng thông tin quản trị trung tâm. Vui lòng đăng nhập với tài khoản Admin để truy cập Dashboard dữ liệu thực.
                                </p>
                                <div>
                                    <Button 
                                        variant="primary" 
                                        size="lg" 
                                        className="rounded-3 px-5 py-3 fw-bold shadow-sm"
                                        onClick={() => navigate('/admin/login')}
                                    >
                                        <i className="bi bi-box-arrow-in-right me-2"></i>
                                        Đăng nhập Quản trị viên
                                    </Button>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        );
    }

    return (
        <div className="admin-dashboard-container py-4">
            <Container fluid className="px-4">
                {/* Header Title with Realtime Pulse */}
                <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                    <div>
                        <h3 className="fw-extrabold text-dark mb-1 d-flex align-items-center gap-2">
                            <i className="bi bi-speedometer2 text-primary"></i>
                            Dashboard Thống Kê Realtime
                        </h3>
                        <p className="text-secondary small mb-0">
                            Cập nhật dữ liệu thực tự động từ cơ sở dữ liệu hệ thống bãi xe IUH Parking
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-3">
                        <Badge bg="success-subtle" className="text-success border border-success-subtle px-3 py-2 rounded-pill fw-semibold d-flex align-items-center gap-2">
                            <span className="pulse-dot bg-success"></span>
                            DỮ LIỆU THỰC — SYNC {realStats.lastUpdated.toLocaleTimeString()}
                        </Badge>
                        <Button variant="outline-primary" size="sm" className="rounded-pill px-3" onClick={loadRealtimeData}>
                            <i className="bi bi-arrow-clockwise me-1"></i> Làm mới
                        </Button>
                    </div>
                </div>

                {/* 4 Main Stat Cards */}
                <Row className="g-3 mb-4">
                    <Col lg={3} md={6}>
                        <Card className="border-0 shadow-sm rounded-4 stat-widget bg-white h-100">
                            <Card.Body className="p-4 d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold d-block mb-1">TỔNG NGƯỜI DÙNG</span>
                                    <h2 className="fw-extrabold text-dark mb-0">{loading ? "..." : realStats.totalUsers.toLocaleString()}</h2>
                                    <span className="text-success small fw-medium mt-1 d-inline-block">
                                        <i className="bi bi-person-check-fill me-1"></i>Tài khoản hệ thống
                                    </span>
                                </div>
                                <div className="widget-icon bg-primary-subtle text-primary rounded-4 d-flex align-items-center justify-content-center fs-3">
                                    <i className="bi bi-people-fill"></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={3} md={6}>
                        <Card className="border-0 shadow-sm rounded-4 stat-widget bg-white h-100">
                            <Card.Body className="p-4 d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold d-block mb-1">NHÂN VIÊN VẬN HÀNH</span>
                                    <h2 className="fw-extrabold text-dark mb-0">{loading ? "..." : realStats.totalEmployees.toLocaleString()}</h2>
                                    <span className="text-primary small fw-medium mt-1 d-inline-block">
                                        <i className="bi bi-shield-shaded me-1"></i>Bảo vệ & Điều hành
                                    </span>
                                </div>
                                <div className="widget-icon bg-success-subtle text-success rounded-4 d-flex align-items-center justify-content-center fs-3">
                                    <i className="bi bi-person-badge-fill"></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={3} md={6}>
                        <Card className="border-0 shadow-sm rounded-4 stat-widget bg-white h-100">
                            <Card.Body className="p-4 d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold d-block mb-1">TỔNG SỨC CHỨA BÃI XE</span>
                                    <h2 className="fw-extrabold text-dark mb-0">{loading ? "..." : realStats.totalCapacity.toLocaleString()}</h2>
                                    <span className="text-warning small fw-medium mt-1 d-inline-block">
                                        <i className="bi bi-p-circle-fill me-1"></i>Vị trí đỗ sẵn có
                                    </span>
                                </div>
                                <div className="widget-icon bg-warning-subtle text-warning rounded-4 d-flex align-items-center justify-content-center fs-3">
                                    <i className="bi bi-p-square-fill"></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg={3} md={6}>
                        <Card className="border-0 shadow-sm rounded-4 stat-widget bg-white h-100">
                            <Card.Body className="p-4 d-flex align-items-center justify-content-between">
                                <div>
                                    <span className="text-secondary small fw-semibold d-block mb-1">TỶ LỆ LẤP ĐẦY THỰC</span>
                                    <h2 className="fw-extrabold text-dark mb-0">{loading ? "..." : `${occupancyRate}%`}</h2>
                                    <span className="text-info small fw-medium mt-1 d-inline-block">
                                        <i className="bi bi-car-front-fill me-1"></i>{realStats.totalOccupied} xe trong bãi
                                    </span>
                                </div>
                                <div className="widget-icon bg-info-subtle text-info rounded-4 d-flex align-items-center justify-content-center fs-3">
                                    <i className="bi bi-pie-chart-fill"></i>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Revenue Chart - Shown First for Admin Visibility */}
                <Row className="g-4 mb-4">
                    <Col lg={12}>
                        <Card className="border-0 shadow-sm rounded-4 bg-white">
                            <Card.Header className="bg-white border-bottom py-3">
                                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                    <i className="bi bi-graph-up-arrow text-success"></i>
                                    Biểu đồ tổng doanh thu bãi xe
                                </h6>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <RevenueChart />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Realtime Parking Lot Capacity Cards */}
                <Row className="g-4 mb-4">
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm rounded-4 bg-white mb-4">
                            <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                                <h6 className="fw-bold text-dark mb-0 d-flex align-items-center gap-2">
                                    <i className="bi bi-building text-primary"></i>
                                    Trạng thái khu vực bãi xe thực tế
                                </h6>
                                <Badge bg="primary-subtle" className="text-primary px-3 py-1.5 rounded-pill">
                                    {realStats.parkingLots.length} Khu vực đỗ
                                </Badge>
                            </Card.Header>
                            <Card.Body className="p-4">
                                {realStats.parkingLots.length === 0 ? (
                                    <p className="text-muted text-center py-4">Đang tải dữ liệu khu vực bãi xe...</p>
                                ) : (
                                    <Row className="g-3">
                                        {realStats.parkingLots.map((lot, index) => {
                                            const capacity = parseInt(lot.capacity || lot.totalSpot || 100, 10);
                                            const occupied = parseInt(lot.currentVehicles || lot.occupiedSpot || 0, 10);
                                            const rate = capacity > 0 ? Math.round((occupied / capacity) * 100) : 0;
                                            return (
                                                <Col md={6} key={index}>
                                                    <div className="p-3 border rounded-3 bg-light">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <span className="fw-bold text-dark">{lot.parkingLotName || lot.name || `Khu bãi ${index + 1}`}</span>
                                                            <Badge bg={rate > 80 ? "danger" : rate > 50 ? "warning" : "success"} className="rounded-pill">
                                                                {rate > 80 ? "Đầy chỗ" : "Hoạt động"}
                                                            </Badge>
                                                        </div>
                                                        <div className="d-flex justify-content-between small text-secondary mb-1">
                                                            <span>Đã đỗ: <b>{occupied}</b> xe</span>
                                                            <span>Tổng: <b>{capacity}</b> vị trí</span>
                                                        </div>
                                                        <ProgressBar 
                                                            now={rate} 
                                                            variant={rate > 80 ? "danger" : rate > 50 ? "warning" : "primary"} 
                                                            style={{ height: "8px" }} 
                                                            className="rounded-pill"
                                                        />
                                                    </div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Quick Admin Actions */}
                    <Col lg={4}>
                        <Card className="border-0 shadow-sm rounded-4 bg-white mb-4">
                            <Card.Header className="bg-white border-bottom py-3">
                                <h6 className="fw-bold text-dark mb-0">Quản lý hệ thống nhanh</h6>
                            </Card.Header>
                            <Card.Body className="p-3">
                                <div className="list-group list-group-flush">
                                    <Link to="/admin/listUser" className="list-group-item list-group-item-action d-flex align-items-center justify-content-between py-3 border-bottom">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="p-2 bg-primary-subtle text-primary rounded-3">
                                                <i className="bi bi-people-fill fs-5"></i>
                                            </div>
                                            <div>
                                                <h6 className="fw-bold mb-0 text-dark">Quản lý người dùng</h6>
                                                <small className="text-secondary">Xem và chỉnh sửa danh sách khách hàng</small>
                                            </div>
                                        </div>
                                        <i className="bi bi-chevron-right text-secondary"></i>
                                    </Link>

                                    <Link to="/admin/listEmployee" className="list-group-item list-group-item-action d-flex align-items-center justify-content-between py-3 border-bottom">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="p-2 bg-success-subtle text-success rounded-3">
                                                <i className="bi bi-person-badge-fill fs-5"></i>
                                            </div>
                                            <div>
                                                <h6 className="fw-bold mb-0 text-dark">Quản lý nhân viên</h6>
                                                <small className="text-secondary">Phân quyền tài khoản nhân viên bảo vệ</small>
                                            </div>
                                        </div>
                                        <i className="bi bi-chevron-right text-secondary"></i>
                                    </Link>

                                    <Link to="/admin/parkinglot" className="list-group-item list-group-item-action d-flex align-items-center justify-content-between py-3">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="p-2 bg-warning-subtle text-warning rounded-3">
                                                <i className="bi bi-p-square-fill fs-5"></i>
                                            </div>
                                            <div>
                                                <h6 className="fw-bold mb-0 text-dark">Khu vực bãi đỗ xe</h6>
                                                <small className="text-secondary">Cấu hình vị trí và sức chứa bãi xe</small>
                                            </div>
                                        </div>
                                        <i className="bi bi-chevron-right text-secondary"></i>
                                    </Link>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default AdminHomepage;
